import { useEffect, useState } from "react";

import { Keyboard } from "lucide-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isInputFocused } from "@/lib/keyboardUtils";
import { cn } from "@/lib/utils";

const shortcuts = [
  { keys: ["?"], description: "Show all shortcuts" },
  { keys: ["⌘", "/"], description: "Show shortcuts (Mac)", platform: "mac" },
  { keys: ["Esc"], description: "Go back" },
  { keys: ["Alt", "←"], description: "Navigate back (Windows/Linux)", platform: "windows" },
  { keys: ["⌘", "←"], description: "Navigate back (Mac)", platform: "mac" },
  { keys: ["⌘", "b"], description: "Toggle sidebar (Mac)", platform: "mac" },
  { keys: ["Ctrl", "b"], description: "Toggle sidebar (Windows/Linux)", platform: "windows" },
];

export const KeyboardShortcutsIndicator = () => {
  const [isAvailable, setIsAvailable] = useState(true);
  const [platform, setPlatform] = useState<"mac" | "windows">("windows");

  useEffect(() => {
    // Detect platform
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    setPlatform(isMac ? "mac" : "windows");

    const checkAvailability = () => {
      setIsAvailable(!isInputFocused());
    };

    // Check on focus/blur events
    const handleFocusChange = () => checkAvailability();
    
    window.addEventListener("focusin", handleFocusChange);
    window.addEventListener("focusout", handleFocusChange);
    
    // Initial check
    checkAvailability();

    return () => {
      window.removeEventListener("focusin", handleFocusChange);
      window.removeEventListener("focusout", handleFocusChange);
    };
  }, []);

  // Filter shortcuts by platform or show all
  const visibleShortcuts = shortcuts.filter(
    s => !s.platform || s.platform === platform
  );

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "fixed bottom-4 right-4 z-50 transition-all duration-300 cursor-help",
              isAvailable ? "opacity-60 hover:opacity-100" : "opacity-20"
            )}
          >
            <div
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-full bg-background/80 backdrop-blur-xs border shadow-lg transition-colors",
                isAvailable ? "border-primary/20" : "border-muted"
              )}
            >
              <Keyboard className={cn(
                "h-4 w-4 transition-colors",
                isAvailable ? "text-primary" : "text-muted-foreground"
              )} />
              <kbd className="px-2 py-0.5 text-xs font-semibold text-foreground bg-muted border border-border rounded">
                ?
              </kbd>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="left" 
          className="max-w-xs p-4 bg-popover"
          sideOffset={8}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <Keyboard className="h-4 w-4 text-primary" />
              <p className="font-semibold text-sm">Keyboard Shortcuts</p>
            </div>
            
            {isAvailable ? (
              <div className="space-y-2">
                {visibleShortcuts.map((shortcut, index) => (
                  <div key={index} className="flex items-center justify-between gap-4 text-xs">
                    <span className="text-muted-foreground">{shortcut.description}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {shortcut.keys.map((key, keyIndex) => (
                        <span key={keyIndex} className="flex items-center gap-1">
                          <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">
                            {key}
                          </kbd>
                          {keyIndex < shortcut.keys.length - 1 && (
                            <span className="text-muted-foreground text-[10px]">+</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Shortcuts disabled while typing in input fields
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
